import { IonCol, IonGrid, IonInput, IonItem, IonLabel, IonList, IonRow } from '@ionic/react'
import { FC, useEffect, useRef, useState } from 'react'
import { fade } from 'common_lib/services/color'
import { getDirMode, getStyleProperty } from 'mobile_web/modules/platform'
import { getSearchDatabase, querySearchDatabase, SearchResult } from 'common_lib/search/functions'
import './search.less'
import { Database } from 'sql.js'
import strings from '../../../../strings'
import classNames from 'classnames'
import { GetScreensShowResponseSectionItems, SearchIndex } from 'mytours-read-api-typescript'
import NavigationService from 'mobile_web/modules/navigation/NavigationService'
import { RootStoreState } from 'mytours-read-common'
import { useSelector } from 'react-redux'
import Loader from '../../../common/components/Loader'
import { useLocation } from 'react-router'

interface Props {
	searchIndexes: {
		[key: string]: SearchIndex
	}
}

export const Search: FC<Props> = ({ searchIndexes }) => {
	const searchInputRef = useRef<HTMLIonInputElement>(null)
	const location = useLocation();

	const projectId = useSelector((state: RootStoreState) => state.settings.appMetaData.project_id)
	const currentLanguage = useSelector((state: RootStoreState) => state.settings.selectedLanguage.code)

	// Search database
	const [searchDatabase, setSearchDatabase] = useState<Database | null>(null)
	useEffect(() => {
		getSearchDatabase(projectId, currentLanguage, searchIndexes[currentLanguage]?.checksum).then(db => {
			setSearchDatabase(db)
		})
	}, [projectId, currentLanguage, searchIndexes])

	// Get search results
	const [searchTerm, setSearchTerm] = useState('')
	const [loading, setLoading] = useState(false)
	const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null)
	useEffect(() => {
		if (searchTerm) {
			if (searchDatabase) {
				// Search database
				const results = querySearchDatabase(searchDatabase, searchTerm)
				setSearchResults(results)
				setLoading(false)
			} else {
				setSearchResults(null)
				setLoading(true)
			}
		} else {
			setSearchResults(null)
			setLoading(false)
		}
	}, [searchTerm, searchDatabase])

	// Auto focus search input
	useEffect(() => {
		const timeout = setTimeout(() => {
			console.log('searchInputRef', searchInputRef)
			searchInputRef.current?.setFocus()
		}, 100)

		return () => clearTimeout(timeout)
	}, [location])

	return (
		<div className="search-container">
			<IonGrid>
				<IonRow>
					<IonCol size="12">
						<div className="search search-wide-grid">
							<IonInput
								ref={searchInputRef}
								value={searchTerm}
								onIonChange={e => {
									setSearchTerm(e.target.value as string)
								}}
								placeholder="Search..."
								className="search-ion-input"
								style={{ borderColor: fade(getStyleProperty('--body-color'), 0.25) }}
							/>
							<div className="search-icons-container">
								<i className="fal fa-magnifying-glass search-loupe" aria-label="Search" aria-hidden="true"></i>

								<button className="search-close-container" onClick={() => setSearchTerm('')}>
									<i className="fal fa-times search-close" aria-label="Close" aria-hidden="true"></i>
								</button>
							</div>
						</div>
					</IonCol>
				</IonRow>

				{searchResults === null ? (
					loading ? (
						<Loader />
					) : (
						<IonRow>
							<IonCol size="12">
								<div dir={getDirMode()} className="search-empty-state-container">
									<i className="fal fa-5x fa-search" />
									<div dir={getDirMode()} className="search-empty-state-block-header">
										<h2 className="search-empty-state-subheading">{strings.live_tours_search}</h2>
									</div>
									<div className="block-content search-empty-state-block-content">{strings.search_description}</div>
								</div>
							</IonCol>
						</IonRow>
					)
				) : (
					<IonRow>
						<IonCol size="12">
							<IonGrid className="results-list section-group search-wide-grid" dir={getDirMode()}>
								{searchResults.length > 0 ? (
									<>
										<h2 className={classNames('subheading results-subheading', getDirMode())}>{strings.results}</h2>
										<IonList className={classNames('section-group-list', getDirMode())} lines="full">
											{searchResults?.map(item => (
												<IonItem
													key={`${item.item_type}${item.item_id}`}
													onClick={() => {
														const screenParams = {
															itemId: item.item_id,
															title: item.title,
														}

														switch (item.item_subtype) {
															case GetScreensShowResponseSectionItems.ItemSubtypeEnum.ScreenPanorama:
																NavigationService.navigateToScreenPanorama(screenParams)
																break
															case GetScreensShowResponseSectionItems.ItemSubtypeEnum.ScreenStory:
																NavigationService.navigateToScreenStory(screenParams)
																break
															case GetScreensShowResponseSectionItems.ItemSubtypeEnum.ScreenWeb:
																NavigationService.navigateToScreenWeb(screenParams)
																break
															case GetScreensShowResponseSectionItems.ItemSubtypeEnum.CollectionList:
															case GetScreensShowResponseSectionItems.ItemSubtypeEnum.CollectionMenu:
																NavigationService.navigateToItem({
																	...screenParams,
																	type: 'list',
																})
																break
															case GetScreensShowResponseSectionItems.ItemSubtypeEnum.CollectionTour:
																NavigationService.navigateToItem({
																	...screenParams,
																	type: 'tour',
																})
																break
														}
													}}
												>
													<IonLabel>{item.title}</IonLabel>
												</IonItem>
											))}
										</IonList>
									</>
								) : searchTerm.length < 3 ? null : (
									<div dir={getDirMode()} className="search-empty-state-container">
										<i className="fal fa-5x fa-xmark" />
										<div dir={getDirMode()} className="search-empty-state-block-header">
											<h2 className="search-empty-state-subheading">{strings.search_none_found}</h2>
										</div>
									</div>
								)}
							</IonGrid>
						</IonCol>
					</IonRow>
				)}
			</IonGrid>
		</div>
	)
}
